import { useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import { shareNodeAsImage } from "@/lib/shareImage";

const findEvent = (list, rnd) => (list || []).find((e) => e.round === rnd);

const CardBadge = ({ color, count, testid }) => (
    <span className="inline-flex items-center gap-1" data-testid={testid}>
        <span className="w-3 h-4 inline-block" style={{ background: color, borderRadius: 1 }} />
        <span className="font-medium">{count}</span>
    </span>
);

function buildRows(coaches, rnd) {
    return coaches
        .map((c) => {
            const r = (c.rounds || []).find((x) => x.round === rnd);
            if (!r) return null;
            return {
                team_id: c.team_id,
                name: c.first_name,
                points: r.points,
                yellow: findEvent(c.yellow_events, rnd)?.count || 0,
                red: findEvent(c.red_events, rnd)?.count || 0,
                og: findEvent(c.own_goal_events, rnd)?.players || [],
            };
        })
        .filter(Boolean)
        .sort((a, b) => b.points - a.points);
}

function RoundSummary({ rows, minPts, threshold, forceWide, p = "round" }) {
    const losers = rows.filter((r) => r.points === minPts).map((r) => r.name);
    const low = rows.filter((r) => r.points < threshold).length;
    const items = [
        { label: "Minste punten", value: losers.length ? `${losers.join(", ")} (${minPts})` : "—", testid: `${p}-summary-loser` },
        { label: `Onder ${threshold}`, value: low, testid: `${p}-summary-low` },
        { label: "Gele kaarten", value: rows.reduce((s, r) => s + r.yellow, 0), testid: `${p}-summary-yellow` },
        { label: "Rode kaarten", value: rows.reduce((s, r) => s + r.red, 0), testid: `${p}-summary-red` },
        { label: "Eigen doelpunten", value: rows.reduce((s, r) => s + r.og.length, 0), testid: `${p}-summary-og`, wide: true },
    ];
    return (
        <div className={`grid gap-3 ${forceWide ? "grid-cols-5" : "grid-cols-2 md:grid-cols-5"}`}>
            {items.map((it) => (
                <div key={it.label} className={`tile p-4 ${it.wide && !forceWide ? "col-span-2 md:col-span-1" : ""}`} data-testid={it.testid}>
                    <div className="label-tiny">{it.label}</div>
                    <div className="font-heading text-2xl mt-1 break-words">{it.value}</div>
                </div>
            ))}
        </div>
    );
}

function RoundRow({ r, i, minPts, threshold, p }) {
    const isLast = r.points === minPts;
    const isLow = r.points < threshold;
    return (
        <tr data-testid={`${p}-row-${r.team_id}`} style={isLast ? { background: "#FF3B3012" } : undefined}>
            <td><span className="rank-badge">{i + 1}</span></td>
            <td>
                <div className="font-medium">{r.name}</div>
                <div className="flex gap-2 mt-1 flex-wrap">
                    {isLast && <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF3B30]">Laatste</span>}
                    {isLow && <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF9500]">Onder {threshold}</span>}
                </div>
            </td>
            <td className="font-heading text-lg" style={{ color: isLow ? "#FF3B30" : undefined }}>{r.points}</td>
            <td>{r.yellow ? <CardBadge color="#FFD60A" count={r.yellow} testid={`${p}-yellow-${r.team_id}`} /> : <span className="text-zinc-500">—</span>}</td>
            <td>{r.red ? <CardBadge color="#FF3B30" count={r.red} testid={`${p}-red-${r.team_id}`} /> : <span className="text-zinc-500">—</span>}</td>
            <td data-testid={`${p}-og-${r.team_id}`}>
                {r.og.length ? <span className="font-medium">{r.og.join(", ")}</span> : <span className="text-zinc-500">—</span>}
            </td>
        </tr>
    );
}

function RoundTable({ rows, minPts, threshold, p = "round" }) {
    return (
        <table className="tac">
            <thead>
                <tr>
                    <th className="w-10">#</th>
                    <th>Coach</th>
                    <th>Punten</th>
                    <th>Geel</th>
                    <th>Rood</th>
                    <th>Eigen doelpunt</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => <RoundRow key={r.team_id} r={r} i={i} minPts={minPts} threshold={threshold} p={p} />)}
            </tbody>
        </table>
    );
}

function RoundShareCard({ innerRef, name, rnd, rows, minPts, threshold }) {
    return (
        <div aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0 }}>
            <div ref={innerRef} style={{ width: 1000, padding: 32, background: "var(--bg)", color: "var(--text)" }} className="space-y-5" data-testid="round-share-card">
                <div>
                    <div className="label-tiny text-[#00FFAA]">{name}</div>
                    <div className="font-heading text-5xl mt-1">Ronde {rnd}</div>
                </div>
                <RoundSummary rows={rows} minPts={minPts} threshold={threshold} forceWide p="share" />
                <div className="tile p-0"><RoundTable rows={rows} minPts={minPts} threshold={threshold} p="share" /></div>
            </div>
        </div>
    );
}

export const RoundsTab = ({ data }) => {
    const shareRef = useRef(null);
    const [sharing, setSharing] = useState(false);
    const roundsAvailable = useMemo(
        () => [...new Set((data.coaches || []).flatMap((c) => (c.rounds || []).map((r) => r.round)))].sort((a, b) => a - b),
        [data.coaches]
    );
    const [rnd, setRnd] = useState(roundsAvailable[roundsAvailable.length - 1]);
    const threshold = data.fees?.low_points_threshold ?? 15;
    if (!roundsAvailable.length) return <div className="tile p-6" data-testid="rounds-empty">Nog geen rondes beschikbaar.</div>;

    const idx = roundsAvailable.indexOf(rnd);
    const rows = buildRows(data.coaches || [], rnd);
    const minPts = rows.length ? Math.min(...rows.map((r) => r.points)) : null;
    const go = (d) => setRnd(roundsAvailable[Math.min(roundsAvailable.length - 1, Math.max(0, idx + d))]);

    return (
        <div className="space-y-5" data-testid="rounds-tab">
            <div className="flex items-center gap-3 flex-wrap">
                <button onClick={() => go(-1)} disabled={idx <= 0} className="btn-ghost inline-flex items-center disabled:opacity-30" data-testid="round-prev-btn" aria-label="Vorige ronde">
                    <ChevronLeft size={16} />
                </button>
                <select value={rnd} onChange={(e) => setRnd(Number(e.target.value))} className="px-4 py-2 border font-heading text-xl" style={{ background: "var(--bg-input)", borderColor: "var(--border)", color: "var(--text)" }} data-testid="round-select">
                    {roundsAvailable.map((r) => <option key={r} value={r}>Ronde {r}</option>)}
                </select>
                <button onClick={() => go(1)} disabled={idx >= roundsAvailable.length - 1} className="btn-ghost inline-flex items-center disabled:opacity-30" data-testid="round-next-btn" aria-label="Volgende ronde">
                    <ChevronRight size={16} />
                </button>
                <button
                    onClick={async () => {
                        setSharing(true);
                        await shareNodeAsImage(shareRef.current, { filename: `ronde-${rnd}-${data.subleague_id}.png`, title: `${data.name} · Ronde ${rnd}`, text: `Overzicht ronde ${rnd} — ${data.name}` });
                        setSharing(false);
                    }}
                    disabled={sharing}
                    className="btn-ghost inline-flex items-center gap-2 md:ml-auto disabled:opacity-50"
                    data-testid="share-round-btn"
                >
                    <Share2 size={14} /> {sharing ? "Bezig…" : "Ronde delen"}
                </button>
            </div>

            <RoundSummary rows={rows} minPts={minPts} threshold={threshold} />

            <div className="tile p-0 overflow-hidden" data-testid="round-table">
                <div className="overflow-x-auto">
                    <RoundTable rows={rows} minPts={minPts} threshold={threshold} />
                </div>
            </div>
            <RoundShareCard innerRef={shareRef} name={data.name} rnd={rnd} rows={rows} minPts={minPts} threshold={threshold} />
        </div>
    );
};
